/**
 * Owner: Masa Bayu
 * File: pwa-register.ts
 * Date:Time: 2026-09-22 06:02
 * Description: QXRead PWA SW registration — shell only · controlled activate · Phase A.
 */

import { PWA_SKIP_WAITING_MESSAGE } from './shell-update';

export const QXREAD_PWA_SHELL_POLICY = Object.freeze({
  cachesShellOnly: true,
  neverGrantsEntitlement: true,
  neverCachesPublicationTruth: true,
  neverUnlocksOfflineWithoutEntitlement: true,
  neverCachesMarketplaceCheckout: true,
  neverCachesPaymentArtifacts: true,
  neverCachesLibraryWithoutEntitlementCheck: true,
  neverGrantsAccessFromOfflineLibraryState: true,
  neverCreatesEntitlementFromLibrarySync: true,
  neverCreatesOrExtendsEntitlementFromLibraryAccess: true,
  neverExecutesReaderFromLibraryLaunchContract: true,
  neverExecutesReaderFromLibraryDeepLink: true,
  neverCreatesEntitlementFromLibraryOrganization: true,
  neverExecutesReaderFromLibraryOrganization: true,
  neverTransfersEntitlementFromLibraryShare: true,
  neverExposesReadingPositionViaLibraryShare: true,
  neverGrantsAccessFromLibraryShareLink: true,
  neverImplementsFlipInQxreadHandoff: true,
  neverMintsEntitlementFromReaderHandoff: true,
  neverExecutesPageMediaInQxreadHandoff: true,
  neverExecutesFlipFromReaderDeepLink: true,
  neverMintsEntitlementFromReaderDeepLink: true,
  neverExecutesFlipInReaderBridgeSession: true,
  neverMintsEntitlementFromReaderBridgeSession: true,
  neverExecutesFlipDuringReadingContinuity: true,
  neverMintsEntitlementFromReadingContinuity: true,
  neverTreatsProgressAsEntitlement: true,
  neverExecutesFlipFromRuntimeIntent: true,
  neverExecutesMediaFromRuntimeIntent: true,
  neverUpgradesPreviewToFullViaIntent: true,
  neverExecutesFlipFromDenialRecovery: true,
  neverTreatsUiReadAsGrantAuthority: true,
  neverSkipsContentAccessGrantRecheck: true,
  neverExecutesFlipDuringReaderJourney: true,
  neverMintsEntitlementFromReaderJourney: true,
  neverHostsQxwMutationInAuthorDashboard: true,
  neverInventsEarningsFromAuthorDashboard: true,
  neverMintsEntitlementFromAuthorDashboard: true,
  neverMutatesQxpFromAuthorDashboard: true,
  neverHostsQxwMutationViaPublishRequest: true,
  neverMutatesQxpViaPublishRequest: true,
  neverTreatsPublishRequestAsQxwDocument: true,
  neverAllowsAuthorSelfApproveWhenPolicyRequires: true,
  neverMintsEntitlementFromStorefrontPreview: true,
  neverExecutesReaderFromStorefrontPreview: true,
  neverTrustsStorefrontClientPriceAsCheckoutAuthority: true,
  neverMutatesQxpFromStorefrontConfig: true,
  neverInventsSalesFromAuthorUi: true,
  neverMintsSaleRecordsWithoutConfirmedOrder: true,
  neverTreatsAuthorSalesAsPaymentAuthority: true,
  neverInventsEarningsFromAuthorUi: true,
  neverExecutesStripePayoutFromAuthorEarnings: true,
  neverMintsEarningLedgerWithoutSaleRecord: true,
  neverTreatsAuthorEarningsAsPaymentAuthority: true,
  neverInventsAnalyticsFromAuthorUi: true,
  neverExposesRawReaderTelemetryInAnalytics: true,
  neverExecutesReaderFromAuthorAnalytics: true,
  neverHostsQxwMutationInAuthorJourney: true,
  neverMintsEntitlementFromAuthorJourney: true,
  neverExecutesPaymentFromAuthorJourney: true,
  neverInventsRevenueFromAdminExecutive: true,
  neverEditsQxpBytesFromAdminExecutive: true,
  neverStoresSecretsInOpsConfig: true,
  neverEmbedsQxWriterInPublishingReview: true,
  neverMutatesQxpFromReviewCase: true,
  neverApprovesPublishWithoutAuditNote: true,
  neverTreatsModerationAsAdamPolicy: true,
  neverExecutesWithdrawalFromAdminCommerceOps: true,
  neverMergesAffRefLedgersInAdminOps: true,
  neverRecordsAuditWithoutAdminOpsActor: true,
  neverTreatsHealthAsEntitlementAuthority: true,
  neverInventsEntitlementFromAdminJourney: true,
  neverTreatsPwaInstallAsContentUnlock: true,
  neverTreatsServiceWorkerAsGrantStore: true,
  neverPreparesOfflinePackageFromPwaInstall: true,
  neverInventsEntitlementFromNotificationPreference: true,
  /** Phase A: waiting worker activates only via SKIP_WAITING (or first-install bootstrap). */
  controlledShellActivate: true,
  serviceWorkerScriptUrl: '/sw.js',
});

export interface QxreadServiceWorkerRegistrationResult {
  readonly registered: boolean;
  readonly reason: string;
  readonly registration?: ServiceWorkerRegistration;
}

type RegisterFn = (
  url: string,
  options?: { readonly updateViaCache?: 'all' | 'imports' | 'none' },
) => Promise<ServiceWorkerRegistration | unknown>;

/**
 * Register the shell service worker when the environment supports it.
 * Does not prepare offline publication packages (Phase B).
 */
export async function registerQxreadServiceWorker(
  registrationApi: { readonly register: RegisterFn } | null,
): Promise<QxreadServiceWorkerRegistrationResult> {
  if (registrationApi === null) {
    return Object.freeze({
      registered: false,
      reason: 'ServiceWorker unsupported in this environment',
    });
  }
  const registration = (await registrationApi.register(
    QXREAD_PWA_SHELL_POLICY.serviceWorkerScriptUrl,
    { updateViaCache: 'none' },
  )) as ServiceWorkerRegistration;

  // First visit: no controller yet — activate immediately without user prompt.
  const installing =
    registration && typeof registration === 'object' && 'installing' in registration
      ? registration.installing
      : null;
  const swContainer =
    typeof navigator !== 'undefined' && 'serviceWorker' in navigator
      ? navigator.serviceWorker
      : null;
  if (
    swContainer !== null &&
    swContainer.controller === null &&
    installing !== null &&
    typeof installing.addEventListener === 'function'
  ) {
    installing.addEventListener('statechange', () => {
      if (registration.waiting !== null) {
        registration.waiting.postMessage(PWA_SKIP_WAITING_MESSAGE);
      }
    });
  }

  return Object.freeze({
    registered: true,
    reason: 'Shell service worker registered (cache ≠ entitlement · controlled activate)',
    registration,
  });
}

export async function checkQxreadShellUpdate(
  registration: ServiceWorkerRegistration | null,
): Promise<boolean> {
  if (registration === null) {
    return false;
  }
  await registration.update();
  return registration.waiting !== null;
}
